import React, { useEffect } from 'react';
import { css, keyframes } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { token, useThemeObserver } from '@atlaskit/tokens';
import { Flex } from '@atlaskit/primitives/compiled';
import Image from '@atlaskit/image';

import LightModeFrame1 from './assets/LightModeFrame1.gif';
import DarkModeFrame1 from './assets/DarkModeFrame1.gif';
import LightModeFrame2 from './assets/LightModeFrame2.gif';
import DarkModeFrame2 from './assets/DarkModeFrame2.gif';
import LightModeFrame3 from './assets/LightModeFrame3.gif';
import DarkModeFrame3 from './assets/DarkModeFrame3.gif';

const styles = cssMap({
	background: {
		position: 'relative',
		backgroundColor: token('color.background.accent.blue.subtler.pressed'),
	},
	frame: {
		position: 'relative',
	},
	image: {
		borderRadius: token('radius.xsmall'),
	},
});

const backgroundGridAnimation = keyframes({
	from: {
		backgroundPosition: '0 0',
	},
	to: {
		backgroundPosition: '-40px 40px',
	},
});

const backgroundGrid = css({
	position: 'absolute',
	height: '100%',
	width: '100%',
	backgroundImage: `
        linear-gradient(90deg, ${token('color.background.accent.blue.subtlest.hovered')} 1px, transparent 1px),
        linear-gradient(0deg, ${token('color.background.accent.blue.subtlest.hovered')} 1px, transparent 1px)
    `,
	backgroundSize: '40px 40px',
	backgroundPosition: '0 0',
	opacity: 0.2,
	animation: `${backgroundGridAnimation} 15s linear infinite`,
});

const accentBoxUpper = css({
	position: 'absolute',
	top: '127px',
	left: '35px',
	width: '330px',
	height: '160px',
	backgroundColor: token('color.background.accent.blue.bolder'),
});

const accentBoxUpperAnimationFrame1 = keyframes({
	'0%': { transform: 'rotate(-2deg)' },
	'100%': { transform: 'rotate(2deg)' },
});

const accentBoxUpperFrame1 = css({
	transform: 'rotate(2deg)',
	animation: `${accentBoxUpperAnimationFrame1} .5s ease-in-out 1`,
});

const accentBoxUpperAnimationFrame2 = keyframes({
	'0%': { transform: 'rotate(2deg)' },
	'100%': { transform: 'rotate(-2deg)' },
});

const accentBoxUpperFrame2 = css({
	transform: 'rotate(-2deg)',
	animation: `${accentBoxUpperAnimationFrame2} .5s ease-in-out 1`,
});

const accentBoxLower = css({
	position: 'absolute',
	bottom: '130px',
	right: '35px',
	width: '200px',
	height: '116px',
	backgroundColor: token('color.background.accent.blue.bolder'),
});

const accentBoxLowerAnimationFrame1 = keyframes({
	'0%': { transform: 'rotate(5deg)' },
	'100%': { transform: 'rotate(-5deg)' },
});

const accentBoxLowerFrame1 = css({
	transform: 'rotate(-5deg)',
	animation: `${accentBoxLowerAnimationFrame1} .5s ease-in-out 1`,
});

const accentBoxLowerAnimationFrame2 = keyframes({
	'0%': { transform: 'rotate(-5deg)' },
	'100%': { transform: 'rotate(5deg)' },
});

const accentBoxLowerFrame2 = css({
	transform: 'rotate(5deg)',
	animation: `${accentBoxLowerAnimationFrame2} .5s ease-in-out 1`,
});

const lightModeFrameMap = [LightModeFrame1, LightModeFrame2, LightModeFrame3];
const darkModeFrameMap = [DarkModeFrame1, DarkModeFrame2, DarkModeFrame3];

export type RovoAnnouncementSplashRightPanelProps = {
	frame: number;
};

type ImageComponentProps = RovoAnnouncementSplashRightPanelProps;

const ImageComponent = ({ frame }: ImageComponentProps) => {
	return (
		<div css={styles.frame}>
			<Image
				// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
				css={styles.image}
				testId={`create-with-rovo-image-frame-${frame}`}
				alt=""
				src={lightModeFrameMap[frame]}
				srcDark={darkModeFrameMap[frame]}
				width={frame === 0 ? 484 : 392}
			/>
		</div>
	);
};

export const RovoAnnouncementSplashRightPanel = ({
	frame,
}: RovoAnnouncementSplashRightPanelProps) => {
	const { colorMode } = useThemeObserver();

	useEffect(() => {
		// Preload images to prevent flickering, only preload images for the current color mode
		const preloadImages = async () => {
			const frameMap = colorMode === 'dark' ? darkModeFrameMap : lightModeFrameMap;

			await Promise.all(
				[...frameMap].map(
					(url: string) =>
						new Promise((resolve, reject) => {
							const img = new window.Image();
							img.src = url;
							img.onload = resolve;
							img.onerror = reject;
						}),
				),
			);
		};

		void preloadImages();
	}, [colorMode]);

	return (
		<Flex xcss={styles.background} justifyContent="center" alignItems="center">
			<div css={backgroundGrid} />

			{frame !== 0 && (
				<div data-testid="create-with-rovo-image-accents">
					<div
						css={[
							accentBoxUpper,
							frame === 1 && accentBoxUpperFrame1,
							frame === 2 && accentBoxUpperFrame2,
						]}
					/>
					<div
						css={[
							accentBoxLower,
							frame === 1 && accentBoxLowerFrame1,
							frame === 2 && accentBoxLowerFrame2,
						]}
					/>
				</div>
			)}

			<ImageComponent frame={frame} />
		</Flex>
	);
};
